import type {
  GroupedRoleChip,
  ResolveScopeName,
} from "../../../../lib/roleChip";
import { type RoleInfo, roleChipsForGrants } from "./roleChipsForGrants";

type GrantPrincipal = {
  id: string;
  scopeId: string;
  builtinRoleId: string | null;
  customRoleId: string | null;
};

type GroupInput<M> = {
  id: string;
  name: string;
  roleGrantsAsScope: readonly M[];
  roleGrantsAsPrincipal: readonly GrantPrincipal[];
};

export type DisplayRow<M = unknown> = {
  id: string;
  name: string;
  memberCount: number;
  members: readonly M[];
  roleBadges: GroupedRoleChip[];
  isBuiltin: boolean;
  iconName: "users-round" | "building-2";
};

export function buildDisplayRows<M>(
  groups: readonly GroupInput<M>[],
  adminGrants: readonly M[],
  memberCounts: ReadonlyMap<string, number>,
  resolveScopeName: ResolveScopeName,
  fullAccessRoleIds: ReadonlySet<string>,
  rolesMap: ReadonlyMap<string, RoleInfo>,
  isActive?: (member: M) => boolean,
  excludeScopeIds?: ReadonlySet<string>
): DisplayRow<M>[] {
  const active = (list: readonly M[]) =>
    isActive ? list.filter(isActive) : list;
  const standardRows: DisplayRow<M>[] = groups.map((g) => {
    const members = active(g.roleGrantsAsScope);
    return {
      id: g.id,
      name: g.name,
      memberCount: memberCounts.get(g.id) ?? members.length,
      members,
      roleBadges: roleChipsForGrants(
        g.roleGrantsAsPrincipal,
        resolveScopeName,
        fullAccessRoleIds,
        rolesMap,
        excludeScopeIds
      ),
      isBuiltin: false,
      iconName: "users-round",
    };
  });
  const activeAdmins = active(adminGrants);
  const tenantAdminsRow: DisplayRow<M> = {
    id: "__tenant-admins__",
    name: "Tenant admins",
    memberCount: activeAdmins.length,
    members: activeAdmins,
    roleBadges: [
      {
        key: "full-access",
        icon: "crown",
        label: "Full access",
        count: 1,
        details: ["Full access"],
        scopeKind: "tenant",
      },
    ],
    isBuiltin: true,
    iconName: "building-2",
  };
  return [...standardRows, tenantAdminsRow];
}
